// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import MagnifyIcon from '@material-ui/icons/Search';

import AnalyticsBase from '../AnalyticsBase';
import AnalyticsFilterBar from '../AnalyticsFilterBar';
import EmptyState from '../../common/EmptyState';
import BreadcrumbNav from '../../nav/BreadcrumbNav';

import { initializeAnalytics } from '../../../state/reducer/analytics/actionCreators/initializeAnalytics';
import { ParamUrlBuilder } from '../../../lib/ParamUrlBuilder';
import * as DateUtils from '../../../lib/dateUtils';
import * as LibraryState from '../../../state/reducer/library/library';
import * as AnalyticsState from '../../../state/reducer/analytics/analytics';
import * as AppState from '../../../state/reducer/app/appState';

import Console from '../../../lib/debug';

type Props = {
    dispatch: any => void
};

const PAGE_KEY = 'lift-progress';
export const DEFAULT_SEARCH_DATE_OFFSET = 30;

export class LiftProgressViewClass extends Component<Props> {
    constructor(props: Props) {
        super(props);
        LibraryState.fetchTeams();
        LibraryState.loadExercises();
    }

    componentDidUpdate(prevProps: {}, prevState: {}) {
        this.loadAnalytics();
    }

    loadAnalytics = () => {
        const dataParams = this.getDataParams();
        if (!dataParams.exercise_id) {
            AppState.stopLoadingAnalytics();
            return;
        }

        this.props.dispatch(initializeAnalytics(PAGE_KEY, dataParams));
    };

    setSearchParams = (params: {}) => {
        const existingParams = AnalyticsState.getSearchParams();
        AnalyticsState.updateSearchParams({
            ...existingParams,
            ...params
        });
    };

    getDataParams = () => {
        const searchParams = AnalyticsState.getSearchParams();

        let endDate = searchParams.end_date;
        if (!DateUtils.isValidDateString(endDate)) {
            endDate = moment();
            endDate = moment(endDate, DateUtils.CALENDAR_FORMAT);
        }

        let startDate = searchParams.start_date;
        if (!DateUtils.isValidDateString(startDate)) {
            startDate = moment(endDate).subtract(
                DEFAULT_SEARCH_DATE_OFFSET,
                'd'
            );
            startDate = moment(startDate, DateUtils.CALENDAR_FORMAT);
        }

        let teamIds = parseInt(searchParams.team_ids, 10);
        if (!teamIds || typeof teamIds !== 'number') {
            teamIds = [];
        } else {
            teamIds = [teamIds];
        }

        const exercise_id = searchParams.exercise_id;
        let useMetric = parseInt(searchParams.use_metric, 10) === 1;
        if (searchParams.use_metric === undefined) {
            useMetric = this.props && this.props.useMetric;
        }

        return {
            team_ids: teamIds,
            exercise_id,
            use_metric: useMetric,
            date_start: moment(startDate).format('YYYY-MM-DD'),
            date_end: moment(endDate).format('YYYY-MM-DD')
        };
    };

    getPageTitle = () => {
        let title =
            this.props.metaTree &&
            this.props.metaTree.instances &&
            this.props.metaTree.instances[PAGE_KEY] &&
            this.props.metaTree.instances[PAGE_KEY].title;
        if (title) {
            return title;
        }
        return 'Team Estimated 1 Rep Max Progress';
    };

    getSelectedTeamId = () => {
        const params = this.getDataParams();
        if (params.team_ids.length === 1) {
            return params.team_ids[0];
        }

        return undefined;
    };

    getSelectedExerciseId = (): number => {
        const params = this.getDataParams();
        if (params.exercise_id) {
            return parseInt(params.exercise_id, 10);
        }
        return undefined;
    };

    getStartDate = () => {
        const dataParams = this.getDataParams();
        return moment(dataParams.date_start);
    };

    getEndDate = () => {
        const dataParams = this.getDataParams();
        return moment(dataParams.date_end);
    };

    onSelectDateRange = ({ startDate, endDate }: State) => {
        const formattedStartDate = startDate.format(DateUtils.CALENDAR_FORMAT);
        if (!endDate) {
            this.setSearchParams({
                start_date: formattedStartDate,
                end_date: formattedStartDate
            });
            return;
        }

        this.setSearchParams({
            start_date: formattedStartDate,
            end_date: endDate.format(DateUtils.CALENDAR_FORMAT)
        });
    };

    onSelectTeam = (ev: Event, { suggestion }: any) => {
        const team_ids = suggestion.allOption === true ? [] : [suggestion.id];

        this.setSearchParams({
            team_ids: team_ids
        });
    };

    onSelectExercise = (e: Event, { suggestion }: any) => {
        const { id } = suggestion;
        this.setSearchParams({
            exercise_id: id
        });
    };

    handleUseMetricChange = (useMetric: boolean) => {
        this.setSearchParams({
            use_metric: useMetric ? 1 : 0
        });
    };

    getInitialState = () => {
        const exericseId = this.getSelectedExerciseId();

        if (exericseId) {
            return null;
        }

        return (
            <div style={{ paddingTop: 60 }}>
                <EmptyState
                    text="Select Exercise to Run Lift Progress"
                    icon={<MagnifyIcon style={{ fontSize: 81 }} />}
                />
            </div>
        );
    };

    onClick = (e: any, row: any, rowIndex: any) => {
        const dataParams = this.getDataParams();
        const paramUrlBuilder = new ParamUrlBuilder(
            '/analytics/lift-one-rep-max-history/'
        );

        paramUrlBuilder
            .setUserId(row.user_id)
            .setStartDate(this.getStartDate())
            .setEndDate(this.getEndDate())
            .setExerciseId(dataParams.exercise_id)
            .setUseMetric(dataParams.use_metric);

        AnalyticsState.navigate(paramUrlBuilder.buildUrl());
    };

    render() {
        Console.render('LiftProgressView');

        const rowEvents = { onClick: this.onClick };
        const initialState = this.getInitialState();

        const crumbs = [
            {
                displayString: 'Analytics',
                link: `/analytics`,
                editable: false
            },
            {
                displayString: this.getPageTitle(),
                link: null,
                editable: false
            }
        ];

        const dataParams = this.getDataParams();

        return (
            <div className="has-filter-bar">
                <BreadcrumbNav crumbs={crumbs} />
                <AnalyticsFilterBar
                    teamSelector={{
                        selectedId: this.getSelectedTeamId(),
                        onSelect: this.onSelectTeam
                    }}
                    exerciseSelector={{
                        selectedId: this.getSelectedExerciseId(),
                        onSelect: this.onSelectExercise,
                        isFocused: false
                    }}
                    dateRangePicker={{
                        startDate: this.getStartDate(),
                        endDate: this.getEndDate(),
                        onChange: this.onSelectDateRange
                    }}
                    useMetricSelector={{
                        useMetric: dataParams.use_metric,
                        onChange: this.handleUseMetricChange
                    }}
                />
                <AnalyticsBase rowEvents={rowEvents} />;
                {initialState}
            </div>
        );
    }
}

const mapStateToProps = (state: any, ownProps: any) => {
    return {
        ...ownProps,
        teams: state.library.teamsTree,
        exerciseRef: state.library.exerciseRef,
        metaTree: state.analytics.metaTree,
        useMetric: state.app.loggedInUser.use_metric
    };
};

export default connect(mapStateToProps)(LiftProgressViewClass);
